/**
 * useIntakeDraftStorage — per-user localStorage persistence for the intake wizard.
 *
 * Keys (per-user, per-session):
 *   sherlock.intake.activeSessionId.<userId>   — the session to auto-restore on mount
 *   sherlock.intake.draftAnswers.<sessionId>   — un-submitted answer state for the open round
 *
 * All localStorage access is wrapped in try/catch (private-browsing mode can
 * restrict storage). Writes for answers are debounced ~250ms to avoid hammering
 * storage on every keystroke.
 *
 * Follow the useSidebarState.ts idiom: lazy read, write with swallowed exceptions.
 */

import { useCallback, useEffect, useRef } from 'react';

/** The in-memory shape of un-submitted answers for a single round. */
export interface DraftAnswerState {
  /** Which round these answers belong to (matches session.round). */
  round: number;
  /** Per-question answer state (keyed by question index). */
  answers: Record<
    number,
    { selected: string | null; selectedMulti: string[]; otherText: string }
  >;
}

const ACTIVE_SESSION_KEY_PREFIX = 'sherlock.intake.activeSessionId.';
const DRAFT_ANSWERS_KEY_PREFIX = 'sherlock.intake.draftAnswers.';
const DEBOUNCE_MS = 250;

// ---------------------------------------------------------------------------
// Pure storage helpers (no React) — each try/catch-guarded
// ---------------------------------------------------------------------------

function activeSessionKey(userId: string): string {
  return `${ACTIVE_SESSION_KEY_PREFIX}${userId}`;
}

function draftAnswersKey(sessionId: string): string {
  return `${DRAFT_ANSWERS_KEY_PREFIX}${sessionId}`;
}

export function readActiveSessionId(userId: string): string | null {
  try {
    return localStorage.getItem(activeSessionKey(userId));
  } catch {
    return null;
  }
}

export function writeActiveSessionId(userId: string, sessionId: string): void {
  try {
    localStorage.setItem(activeSessionKey(userId), sessionId);
  } catch {
    /* swallow */
  }
}

export function clearActiveSessionId(userId: string): void {
  try {
    localStorage.removeItem(activeSessionKey(userId));
  } catch {
    /* swallow */
  }
}

export function readDraftAnswers(sessionId: string): DraftAnswerState | null {
  try {
    const raw = localStorage.getItem(draftAnswersKey(sessionId));
    if (!raw) return null;
    return JSON.parse(raw) as DraftAnswerState;
  } catch {
    return null;
  }
}

export function writeDraftAnswers(sessionId: string, state: DraftAnswerState): void {
  try {
    localStorage.setItem(draftAnswersKey(sessionId), JSON.stringify(state));
  } catch {
    /* swallow */
  }
}

export function clearDraftAnswers(sessionId: string): void {
  try {
    localStorage.removeItem(draftAnswersKey(sessionId));
  } catch {
    /* swallow */
  }
}

// ---------------------------------------------------------------------------
// React hook — debounced writes
// ---------------------------------------------------------------------------

export interface UseIntakeDraftStorage {
  /** Save the active session id so the wizard can auto-restore it. */
  saveActiveSession: (sessionId: string) => void;
  /** Clear the stored active session id (on done / reset / discard). */
  clearActiveSession: () => void;
  /** Debounced write of the current round's un-submitted answers. */
  saveDraftAnswers: (sessionId: string, state: DraftAnswerState) => void;
  /** Clear draft answers (on successful submit / reset). */
  clearDraftAnswers: (sessionId: string) => void;
}

/**
 * @param userId - the authenticated user's id. Pass null/undefined when auth
 *   is not yet resolved; the hook is a no-op until userId is non-null.
 */
export function useIntakeDraftStorage(userId: string | undefined | null): UseIntakeDraftStorage {
  // Debounce timer ref for answer writes.
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cancel any pending debounced write on unmount.
  useEffect(() => {
    return () => {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const saveActiveSession = useCallback(
    (sessionId: string) => {
      if (!userId) return;
      writeActiveSessionId(userId, sessionId);
    },
    [userId],
  );

  const clearActiveSession = useCallback(() => {
    if (!userId) return;
    clearActiveSessionId(userId);
  }, [userId]);

  const saveDraftAnswersDebounced = useCallback(
    (sessionId: string, state: DraftAnswerState) => {
      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        writeDraftAnswers(sessionId, state);
        debounceTimer.current = null;
      }, DEBOUNCE_MS);
    },
    [],
  );

  const clearDraftAnswersCallback = useCallback((sessionId: string) => {
    // Cancel any pending write first so we don't re-write immediately after clear.
    if (debounceTimer.current !== null) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    clearDraftAnswers(sessionId);
  }, []);

  return {
    saveActiveSession,
    clearActiveSession,
    saveDraftAnswers: saveDraftAnswersDebounced,
    clearDraftAnswers: clearDraftAnswersCallback,
  };
}
